import { faCircleXmark, faLock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, Transition } from '@headlessui/react'
import { useNavigate } from 'react-router-dom'
import { Fragment, useMemo } from 'react'

import useAuth from '../hooks/useAuth'

const ViewServiceSlideout = ({ open, setOpen, service }) => {
  const { auth, createInfo } = useAuth()
  const navigate = useNavigate()

  // Check if user profile is complete
  const isProfileComplete = () => {
    return auth?.user?.onboarding_complete === 1 ? true : false
  }


  // Process payment - placeholder function
  const processPayment = (product) => {
    if (!!product?.paymentLink) {
      window.location.href = product.paymentLink;
    } else {
      createInfo('error', 'Payment link not available.')
    }
  }

  const borderColour = service?.colour || '#FDD2B7'
  const iconColor = service?.iconColour || '#FDD2B7'
  const title = service?.name || 'Service Name'
  const description = service?.description || 'Get faster support from clinicians for minor illness or non-urgent conditions.'
  const products = service?.products || []

  const productTitles = useMemo(
    () => products.map((p) => p?.title).filter(Boolean),
    [products]
  )

  const handleProductClick = (product) => {
    // Check if profile is complete first
    if (!isProfileComplete()) {
      alert('Please complete your profile to access this service.')
      return
    }

      if(product.external) {
        window.open(product.link, '_blank')
        return;
      }
      // Navigate to the original link
      if (product.bookable) {
        navigate('/book-assessment')
      } else {
        navigate(`/book-service/${product.link}`, {state: { product }})
      }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={() => { setOpen(false);}}>
        <Transition.Child
          as="div"
          className="fixed inset-0 bg-gray-500 bg-opacity-30 backdrop-blur-[3px]"
          enter="transition-opacity ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-3xl pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-3xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-[#f8f6fb] shadow-xl relative">
                    <div className=" px-4 py-6 sm:px-6">
                      <div className="flex items-start justify-between">
                        {/* Header Section */}
                        <div className="flex items-start gap-4">
                          <div className={`${iconColor} px-4 py-3 rounded-xl`}>
                            <FontAwesomeIcon
                              icon={service?.icon}
                              className="text-[#41264B] text-xl"
                            />
                          </div>
                          <div>
                            <h2 className="text-2xl font-semibold text-[#41264B]">
                              {title} Services
                            </h2>
                            <p className="text-sm text-gray-600">
                              {description}
                            </p>
                          </div>
                        </div>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative  text-[#41264B] hover:text-nav-dark focus:outline-none focus:ring-2 focus:ring-nav"
                            onClick={() => { setOpen(false); }}
                          >
                            <span className="sr-only">Close panel</span>
                            <FontAwesomeIcon
                              icon={faCircleXmark}
                              className="text-xl"
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    <section className="p-6 flex flex-col gap-6 h-full">
                      {products?.map((product, index) => {
                        return (
                          <div
                            className="bg-white rounded-xl shadow-md p-5 relative group"
                            key={index}
                          >
                            <h3
                              className={`${service?.textColour} font-semibold text-lg mb-2`}
                            >
                              {product.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                              {product.description}
                            </p>
                            {/* Check if user has access to show appropriate button */}
                            {isProfileComplete() ? (
                              <button
                                onClick={() => handleProductClick(product)}
                                className={`${iconColor} border-2 ${borderColour} text-[#41264B] font-medium px-5 py-1.5 rounded-md hover:bg-white transition`}
                              >
                                {product.bookable ? 'Book Assessment' : 'Learn More'}
                              </button>
                            ) : (
                              <button
                                className={`${iconColor} border-2 ${borderColour} text-[#41264B] font-medium px-5 py-1.5 rounded-md transition cursor-not-allowed opacity-75 flex items-center gap-2`}
                                disabled
                              >
                                <FontAwesomeIcon icon={faLock} className="text-sm" />
                                {product.bookable ? 'Book Assessment' : 'Learn More'}
                              </button>
                            )}

                            {/* Full card overlay for restricted access */}
                            {!isProfileComplete() && (
                              <div className="absolute inset-0 bg-gray-900 bg-opacity-90 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                <div className="text-white text-center p-6">
                                  <FontAwesomeIcon icon={faLock} className="text-3xl mb-4" />
                                  <p className="text-sm leading-relaxed">
                                    {!isProfileComplete()
                                      && 'Please complete your profile to access this service'
                                    }
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </section>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default ViewServiceSlideout
